import apiClient from './api';

/**
 * Lab Data Service
 * Handles lab data-related API calls (blood pressure, sugar, weight, etc.)
 */

/**
 * Add lab data
 * @param {Array} data - Array of lab data objects
 * @example
 * addLabData([
 *   {
 *     dateadd: "2025-12-15 12:00:00",
 *     name: "sys/dias",
 *     value: "120/80"
 *   },
 *   {
 *     dateadd: "2025-12-15 12:00:00",
 *     name: "weight",
 *     value: "65"
 *   }
 * ])
 * @returns {Promise} Response
 */
export const addLabData = async (data) => {
  try {

    const response = await apiClient.post('/AddLabData.php', {
      data
    });

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get lab data with pagination
 * @param {string} dateadd - Date filter (optional, format: YYYY-MM-DD or YYYY-MM-DD HH:mm:ss)
 * @param {number} page - Page number (default: 1)
 * @returns {Promise} Response with lab data list
 */
export const getLabData = async (dateadd = '', page = 1) => {
  try {
    const response = await apiClient.post('/GetLabData.php', {
      dateadd,
      page
    });

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get lab data by name (specific type of measurement)
 * @param {string} name - Lab data name (e.g., "sys/dias", "weight", "sugar")
 * @returns {Promise} Response with filtered lab data
 */
export const getLabDataByName = async (name) => {
  try {
    const response = await apiClient.post('/GetLabDataByName.php', {
      name
    });

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to add blood pressure data
 * @param {string} systolic - Systolic pressure (upper number)
 * @param {string} diastolic - Diastolic pressure (lower number)
 * @param {string} pulse - Pulse rate (optional)
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addBloodPressure = async (systolic, diastolic, pulse = null, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');

  const data = [
    {
      dateadd,
      name: 'sys/dias',
      value: `${systolic}/${diastolic}`
    }
  ];

  // Add pulse if provided
  if (pulse) {
    data.push({
      dateadd,
      name: 'pulse',
      value: pulse.toString()
    });
  }

  return addLabData(data);
};

/**
 * Helper function to add weight data
 * @param {string} weight - Weight in kg
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addWeight = async (weight, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');

  return addLabData([
    {
      dateadd,
      name: 'weight',
      value: weight.toString()
    }
  ]);
};

/**
 * Helper function to add blood sugar data
 * @param {string} sugar - Blood sugar level
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addBloodSugar = async (sugar, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');

  return addLabData([
    {
      dateadd,
      name: 'fps/fpg',
      value: sugar.toString()
    }
  ]);
};

/**
 * Helper function to add pulse data
 * @param {string} pulse - Pulse rate (beats per minute)
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addPulse = async (pulse, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');

  return addLabData([
    {
      dateadd,
      name: 'pulse',
      value: pulse.toString()
    }
  ]);
};

/**
 * Get blood pressure history
 * @returns {Promise} Response with blood pressure data
 */
export const getBloodPressureHistory = async () => {
  // ดึงทั้ง sys/dias (user บันทึกเอง) และ sys+dia แยก (โรงพยาบาล)
  const [sysDiasRes, sysRes, diaRes] = await Promise.all([
    getLabDataByName('sys/dias'),
    getLabDataByName('sys'),
    getLabDataByName('dia')
  ]);
  const sysDiasData = sysDiasRes?.data || [];
  const sysData = sysRes?.data || [];
  const diaData = diaRes?.data || [];

  // รวม sys+dia แยกกัน
  const diaMap = new Map();
  diaData.forEach(item => {
    const dateKey = item.dateadd?.split('.')[0] || item.dateadd;
    diaMap.set(dateKey, item.value);
  });

  const combinedSysDia = sysData
    .map(item => {
      const dateKey = item.dateadd?.split('.')[0] || item.dateadd;
      const diaValue = diaMap.get(dateKey);
      if (diaValue) {
        return { ...item, value: `${item.value}/${diaValue}` };
      }
      return null;
    })
    .filter(Boolean);

  // รวมทั้งสองชุด แล้ว sort ตามวันที่ล่าสุด
  const all = [...sysDiasData, ...combinedSysDia]
    .sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));

  return { data: all };
};

/**
 * Get pulse history
 * @returns {Promise} Response with pulse data
 */
export const getPulseHistory = async () => {
  return getLabDataByName('pulse');
};

/**
 * Get weight history
 * @returns {Promise} Response with weight data
 */
export const getWeightHistory = async () => {
  return getLabDataByName('weight');
};

/**
 * Get blood sugar history
 * @returns {Promise} Response with blood sugar data
 */
export const getBloodSugarHistory = async () => {
  return getLabDataByName('fbs/fpg');
};

/**
 * Update lab data
 * @param {string} dateadd - Original date of the data to update
 * @param {string} name - Lab data name (e.g., "sys/dias", "weight", "sugar")
 * @param {string} value - New value
 * @param {string} uploader - Uploader type: "m" (manual/daily) or "h" (hospital)
 * @returns {Promise} Response
 * @example
 * updateLabData("2025-12-02 12:13:33", "sys/dias", "120/80", "m")
 */
export const updateLabData = async (dateadd, name, value, uploader = "h") => {
  try {
    const data = [{
      dateadd,
      name,
      value,
      uploader
    }];

    const response = await apiClient.post('/UpdateLabData.php', { data });

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to update blood pressure data
 * @param {string} dateadd - Original date of the record
 * @param {string} systolic - New systolic pressure
 * @param {string} diastolic - New diastolic pressure
 * @returns {Promise} Response
 */
export const updateBloodPressure = async (dateadd, systolic, diastolic) => {
  // ข้อมูลเก่าเก็บเป็น sys + dia แยก, ข้อมูลใหม่เก็บเป็น sys/dias
  // update ทั้ง 2 แบบเพื่อ compatibility
  // Blood pressure uses uploader: "m" (manual/daily checkup)
  await Promise.all([
    updateLabData(dateadd, 'sys', systolic.toString(), 'm'),
    updateLabData(dateadd, 'dia', diastolic.toString(), 'm'),
    updateLabData(dateadd, 'sys/dias', `${systolic}/${diastolic}`, 'm')
  ]);
};

/**
 * Helper function to update weight data
 * @param {string} dateadd - Original date of the record
 * @param {string} weight - New weight in kg
 * @returns {Promise} Response
 */
export const updateWeight = async (dateadd, weight) => {
  // Weight uses uploader: "m" (manual/daily checkup)
  return updateLabData(dateadd, 'weight', weight.toString(), 'm');
};

/**
 * Helper function to update blood sugar data
 * @param {string} dateadd - Original date of the record
 * @param {string} sugar - New blood sugar level
 * @returns {Promise} Response
 */
export const updateBloodSugar = async (dateadd, sugar) => {
  // update ทั้ง fbs (ชื่อเดิม) และ fbs/fpg (ชื่อใหม่)
  // Blood sugar uses uploader: "h" (hospital checkup) as default
  await Promise.all([
    updateLabData(dateadd, 'fbs', sugar.toString(), 'h'),
    updateLabData(dateadd, 'fbs/fpg', sugar.toString(), 'h')
  ]);
};

/**
 * Helper function to add Creatinine data
 * @param {string} creatinine - Creatinine value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addCreatinine = async (creatinine, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');

  return addLabData([
    {
      dateadd,
      name: 'creatinine',
      value: creatinine.toString()
    }
  ]);
};

/**
 * Helper function to add BUN data
 * @param {string} bun - BUN value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addBUN = async (bun, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');

  return addLabData([
    {
      dateadd,
      name: 'bun',
      value: bun.toString()
    }
  ]);
};

/**
 * Helper function to add eGFR data
 * @param {string} egfr - eGFR value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addEGFR = async (egfr, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');

  return addLabData([
    {
      dateadd,
      name: 'egfr',
      value: egfr.toString()
    }
  ]);
};

/**
 * Helper function to update Creatinine data
 * @param {string} dateadd - Original date of the record
 * @param {string} creatinine - New Creatinine value
 * @returns {Promise} Response
 */
export const updateCreatinine = async (dateadd, creatinine) => {
  return updateLabData(dateadd, 'creatinine', creatinine.toString());
};

/**
 * Helper function to update BUN data
 * @param {string} dateadd - Original date of the record
 * @param {string} bun - New BUN value
 * @returns {Promise} Response
 */
export const updateBUN = async (dateadd, bun) => {
  return updateLabData(dateadd, 'bun', bun.toString());
};

/**
 * Helper function to update eGFR data
 * @param {string} dateadd - Original date of the record
 * @param {string} egfr - New eGFR value
 * @returns {Promise} Response
 */
export const updateEGFR = async (dateadd, egfr) => {
  return updateLabData(dateadd, 'egfr', egfr.toString());
};

// ===============================================
// CBC (Complete Blood Count) - ความสมบูรณ์ของเลือด
// ===============================================

/**
 * Helper function to add WBC (White Blood Cell) data
 * @param {string} wbc - WBC value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addWBC = async (wbc, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'wbc', value: wbc.toString() }]);
};

/**
 * Helper function to add RBC (Red Blood Cell) data
 * @param {string} rbc - RBC value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addRBC = async (rbc, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'rbc', value: rbc.toString() }]);
};

/**
 * Helper function to add Hemoglobin data
 * @param {string} hb - Hemoglobin value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addHemoglobin = async (hb, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'hb', value: hb.toString() }]);
};

/**
 * Helper function to add Hematocrit data
 * @param {string} hct - Hematocrit value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addHematocrit = async (hct, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'hct', value: hct.toString() }]);
};

/**
 * Helper function to add Platelet data
 * @param {string} platelet - Platelet value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addPlatelet = async (platelet, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'plt', value: platelet.toString() }]);
};

// ===============================================
// HbA1c - น้ำตาลสะสม
// ===============================================

/**
 * Helper function to add HbA1c data
 * @param {string} hba1c - HbA1c value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addHbA1c = async (hba1c, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'hba1c', value: hba1c.toString() }]);
};

// ===============================================
// Lipid Profile - ไขมัน
// ===============================================

/**
 * Helper function to add Total Cholesterol data
 * @param {string} cholesterol - Total Cholesterol value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addCholesterol = async (cholesterol, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'chol', value: cholesterol.toString() }]);
};

/**
 * Helper function to add HDL data
 * @param {string} hdl - HDL value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addHDL = async (hdl, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'hdl', value: hdl.toString() }]);
};

/**
 * Helper function to add LDL data
 * @param {string} ldl - LDL value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addLDL = async (ldl, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'ldl', value: ldl.toString() }]);
};

/**
 * Helper function to add Triglyceride data
 * @param {string} triglyceride - Triglyceride value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addTriglyceride = async (triglyceride, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'triglycerideresult', value: triglyceride.toString() }]);
};

// ===============================================
// Liver Function - ตับ
// ===============================================

/**
 * Helper function to add SGOT (AST) data
 * @param {string} sgot - SGOT value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addSGOT = async (sgot, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'sgot', value: sgot.toString() }]);
};

/**
 * Helper function to add SGPT (ALT) data
 * @param {string} sgpt - SGPT value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addSGPT = async (sgpt, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'sgpt', value: sgpt.toString() }]);
};

/**
 * Helper function to add Alkaline Phosphatase data
 * @param {string} alp - ALP value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addAlkalinePhosphatase = async (alp, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'alp', value: alp.toString() }]);
};

// ===============================================
// Uric Acid - กรดยูริค
// ===============================================

/**
 * Helper function to add Uric Acid data
 * @param {string} uricAcid - Uric Acid value
 * @param {string} datetime - Date and time (optional, default: now)
 * @returns {Promise} Response
 */
export const addUricAcid = async (uricAcid, datetime = null) => {
  const dateadd = datetime || new Date().toISOString().slice(0, 19).replace('T', ' ');
  return addLabData([{ dateadd, name: 'uric', value: uricAcid.toString() }]);
};

// ===============================================
// Lab Results from MFU API - ผลตรวจจาก MFU
// ===============================================

/**
 * Get Urine Test Results for logged-in user
 * Retrieves all urine test parameters and combines them
 * @returns {Promise} Response with urine test results
 */
export const getUrineTestResults = async () => {
  try {
    // ชื่อ parameter ใน DB จริง → ชื่อ field ที่ใช้แสดงผล
    const urineParamMap = {
      'urinalysiscolorresul': 'urinalysis_color',
      'urinalysisapperancer': 'urinalysis_appearance',
      'urinalysisphresult': 'urinalysis_ph',
      'urinalysisspecificgr': 'urinalysis_specific_gravity',
      'urinalysisproteinres': 'urinalysis_protein',
      'urinalysissugarresul': 'urinalysis_glucose',
      'urinalysisketoneresu': 'urinalysis_ketone',
      'urinalysisbloodresul': 'urinalysis_blood',
      'urinalysisleukocyter': 'urinalysis_leukocytes',
      'urinalysisnitritere': 'urinalysis_nitrite',
      'urinalysisbilirubinr': 'urinalysis_bilirubin',
      'urinalysisurobilino': 'urinalysis_urobilinogen',
      'urinalysisrbcresult': 'urinalysis_rbc',
      'urinalysiswbcresult': 'urinalysis_wbc',
      'urinalysisbactcellre': 'urinalysis_bacteria',
      'urinalysisepithelial': 'urinalysis_epithelial'
    };

    // Fetch all urine parameters in parallel
    const promises = Object.keys(urineParamMap).map(param =>
      getLabDataByName(param).catch(err => {
        return { data: [] };
      })
    );

    const results = await Promise.all(promises);

    // Combine all parameters by date
    const recordsMap = new Map();
    const paramKeys = Object.keys(urineParamMap);

    paramKeys.forEach((param, index) => {
      const paramData = results[index]?.data || [];
      if (Array.isArray(paramData)) {
        // Group by วันที่เดียวกัน (ไม่สนเวลา) แล้วเอาเฉพาะ record แรก (id ต่ำสุด = ค่าจริง)
        // เพราะ API อาจ return หลาย record ต่อ parameter (ค่าจริง, ค่าปกติ, unit)
        const seenDates = new Set();
        const sortedData = [...paramData].sort((a, b) => parseInt(a.id) - parseInt(b.id));
        sortedData.forEach(item => {
          const fullDate = item.dateadd;
          // แยกเฉพาะวันที่ (YYYY-MM-DD) ไม่เอาเวลา
          const dateOnly = fullDate ? fullDate.split(' ')[0] : fullDate;
          const dateKey = `${dateOnly}_${param}`; // ใช้ dateOnly + param เป็น key ไม่ให้ซ้ำ

          if (seenDates.has(dateKey)) return; // ข้าม record ซ้ำ (ค่าปกติ/unit)
          seenDates.add(dateKey);

          if (!recordsMap.has(dateOnly)) {
            recordsMap.set(dateOnly, {
              dateadd: fullDate,
              visit_date: dateOnly,
              hospital_name: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง'
            });
          }
          recordsMap.get(dateOnly)[urineParamMap[param]] = item.value;
        });
      }
    });

    // Convert map to array and sort by date (newest first)
    const list = Array.from(recordsMap.values()).sort((a, b) =>
      new Date(b.dateadd) - new Date(a.dateadd)
    );

    return {
      result: list.length > 0,
      list: list
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get Stool Test Results for logged-in user
 * Retrieves all stool test parameters and combines them
 * @returns {Promise} Response with stool test results
 */
export const getStoolTestResults = async () => {
  try {
    // List of stool test parameters to retrieve (matching form field names)
    const stoolParamMap = {
      'stoolColor': 'stool_color_result',
      'stoolConsistency': 'stool_consistency_result',
      'stoolRBC': 'stool_rbc_result',
      'stoolWBC': 'stool_wbc_result',
      'stoolParasite': 'stool_parasite_result',
      'stoolOccultBlood': 'stool_occult_blood_result'
    };

    // Fetch all stool parameters in parallel
    const promises = Object.keys(stoolParamMap).map(param =>
      getLabDataByName(param).catch(err => {
        return { data: [] };
      })
    );

    const results = await Promise.all(promises);

    // Combine all parameters by date
    const recordsMap = new Map();
    const paramKeys = Object.keys(stoolParamMap);

    paramKeys.forEach((param, index) => {
      const paramData = results[index]?.data || [];
      if (Array.isArray(paramData)) {
        paramData.forEach(item => {
          const date = item.dateadd;
          if (!recordsMap.has(date)) {
            recordsMap.set(date, {
              dateadd: date,
              visit_date: date,
              hospital_name: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง'
            });
          }
          // Map to stool result field name
          recordsMap.get(date)[stoolParamMap[param]] = item.value;
        });
      }
    });

    // Convert map to array and sort by date (newest first)
    const list = Array.from(recordsMap.values()).sort((a, b) =>
      new Date(b.dateadd) - new Date(a.dateadd)
    );

    return {
      result: list.length > 0,
      list: list
    };
  } catch (error) {
    throw error;
  }
};
